import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, Clock, MapPin, Users, ArrowLeft, Settings, Share, BarChart3 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { useToast } from "@/hooks/use-toast";
import { eventService } from "@/services/eventService";
import { Event } from "@/types/event";
import NavigationBar from "@/components/NavigationBar";
import { GuestManagement } from "@/components/GuestManagement";
import { EventImageGenerator } from "@/components/EventImageGenerator";

const EventDetail = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    subscription,
    hasClientPortalAccess,
    hasAdvancedAnalytics,
    checkCanAddGuests
  } = useSubscription();
  const { toast } = useToast();

  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [guests, setGuests] = useState<any[]>([]);

  useEffect(() => {
    if (!eventId) {
      navigate('/dashboard');
      return;
    }
    loadEventData();
  }, [eventId]);

  const loadEventData = async () => {
    try {
      setLoading(true);
      // Load event details
      console.log('🔍 [EventDetail] Loading event data for eventId:', eventId);
      console.log('🔍 [EventDetail] Current user:', user);
      console.log('🔍 [EventDetail] User ID specifically:', user?.id);
      
      const eventData = await eventService.getEvent(eventId!);
      console.log('🔍 [EventDetail] Loaded event data:', eventData);
      console.log('🔍 [EventDetail] Event userId specifically:', eventData?.userId);
      
      if (!eventData) {
        console.log('🔍 [EventDetail] Event not found');
        toast({
          title: "Event not found",
          description: "The event you're looking for doesn't exist.",
          variant: "destructive",
        });
        navigate('/dashboard');
        return;
      }

      // Permission already validated at Dashboard level - no need to re-check
      console.log('🔍 [EventDetail] Loading event data for user:', user?.id);

      setEvent(eventData);
      
      // Load guests
      const guestsData = await eventService.getEventGuests(eventId!);
      setGuests(guestsData);
    } catch (error) {
      console.error('Error loading event data:', error);
      toast({
        title: "Error",
        description: "Failed to load event data.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleShareEvent = () => {
    if (!event) return;
    const shareUrl = `https://www.eventdatepicker.com/vote/${event.shareableLink}`;
    navigator.clipboard.writeText(shareUrl);
    toast({
      title: "Link copied!",
      description: "Event link has been copied to your clipboard",
    });
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString();
  };

  const getEventStatusBadge = () => {
    if (!event) return null;
    if (!event.isActive) {
      return <Badge variant="secondary">Closed</Badge>;
    }
    if (event.deadline && new Date(event.deadline) < new Date()) {
      return <Badge variant="destructive">Expired</Badge>;
    }
    return <Badge variant="default">Active</Badge>;
  };

  // Check if user has guest management access (Pro/Enterprise)
  const hasGuestManagementAccess = subscription.subscription_tier === 'professional' || 
                                   subscription.subscription_tier === 'enterprise';

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-background">
        <NavigationBar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">Loading event details...</div>
        </div>
      </div>
    );
  }

  if (!event) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-background">
      <NavigationBar />
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-foreground">{event.name}</h1>
            <p className="text-muted-foreground">Hosted by {event.hostName}</p>
          </div>
          {getEventStatusBadge()}
        </div>

        {/* Event Overview Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Event Overview
            </CardTitle>
            <CardDescription>
              {event.description || "No description provided"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">
                  {event.options.filter(opt => opt.type === 'date').length} date options
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">
                  {event.options.filter(opt => opt.type === 'time').length} time options
                </span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">
                  {event.options.filter(opt => opt.type === 'location').length} location options
                </span>
              </div>
            </div>
            
            {event.deadline && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Voting deadline: {formatDate(event.deadline)}
                </p>
              </div>
            )}

            <div className="flex gap-2 mt-4">
              <Button 
                variant="outline" 
                onClick={handleShareEvent}
                className="flex items-center gap-2"
              >
                <Share className="w-4 h-4" />
                Share Event
              </Button>
              <EventImageGenerator event={event} />
              <Button 
                variant="outline"
                onClick={() => navigate(`/analytics?event=${event.id}`)}
                className="flex items-center gap-2"
              >
                <BarChart3 className="w-4 h-4" />
                View Analytics
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for different management sections */}
        <Tabs defaultValue="guests" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="guests" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Guest Management ({guests.length})
            </TabsTrigger>
            <TabsTrigger value="voting" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Voting Results ({event.totalVoters} voted)
            </TabsTrigger>
          </TabsList>

          <TabsContent value="guests" className="mt-6">
            {hasGuestManagementAccess ? (
              <GuestManagement 
                eventId={event.id}
                guests={guests}
                onGuestsUpdate={loadEventData}
              />
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>Guest Management</CardTitle>
                  <CardDescription>
                    Upgrade to Pro or Enterprise to manage guests after event creation
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Pro Feature</h3>
                    <p className="text-muted-foreground mb-4">
                      Add and manage guests after creating your event with a Pro or Enterprise subscription.
                    </p>
                    <Button onClick={() => navigate('/subscription')}>
                      Upgrade Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="voting" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Voting Results</CardTitle>
                <CardDescription>
                  Current voting status for your event options
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {event.options.map((option) => (
                    <div key={option.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-2">
                          {option.type === 'date' && <Calendar className="w-4 h-4" />}
                          {option.type === 'time' && <Clock className="w-4 h-4" />}
                          {option.type === 'location' && <MapPin className="w-4 h-4" />}
                          <span className="font-medium">{option.value}</span>
                        </div>
                        <Badge variant="outline">
                          {option.votes} votes
                        </Badge>
                      </div>
                      <div className="w-full bg-secondary rounded-full h-2">
                        <div 
                          className="bg-primary h-2 rounded-full transition-all"
                          style={{ 
                            width: event.totalVoters > 0 ? `${(option.votes / event.totalVoters) * 100}%` : '0%' 
                          }}
                        />
                      </div>
                    </div>
                  ))}
                  {event.options.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">
                      No voting options available for this event.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default EventDetail;